import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Plus } from 'lucide-react'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { PageHeader } from '@/components/ui/page-header'
import {
  deleteRobotEvent,
  fetchRobotEvents,
  updateRobotEvent,
} from '@/api/robot-events.api'
import { buildRobotEventColumns } from './columnsEvent'
import type { RobotEventRow } from './columnsEvent'
import { RobotEventFilterPanel } from './RobotEventFilterPanel'
import type { RobotEventFilterValues } from './RobotEventFilterPanel'
import { RobotEventDrawer } from './RobotEventDrawer'
import { formatEndDate, formatStartDate } from '@/utils/format'
import { exportEventRobot, useTableExport } from '@/api/dowload-excel.api'
import type { ExportPayload } from '@/api/dowload-excel.api'
import { usePermission } from '@/hooks/use-permission'

dayjs.extend(utc)

const DEFAULT_FILTER: RobotEventFilterValues = {
  keyword: '',
  status: undefined,
  eventType: undefined,
  time: undefined,
}

interface RobotEventsTabProps {
  robotId: string
}

type RobotEventsFilterParams = {
  page: number
  size: number
  keySearch?: string
  status?: number
  eventType?: number
  startDateFrom?: string
  startDateTo?: string
}

export function RobotEventsTab({ robotId }: RobotEventsTabProps) {
  const queryClient = useQueryClient()
  const canExport = usePermission('robot-event-export')

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [showFilter, setShowFilter] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false)
  const [deletingEvent, setDeletingEvent] = useState<RobotEventRow | null>(null)
  const [drawerMode, setDrawerMode] = useState<'create' | 'update' | 'view'>(
    'create',
  )
  const [selectedEvent, setSelectedEvent] = useState<RobotEventRow | null>(null)
  const [filter, setFilter] = useState<RobotEventFilterValues>(DEFAULT_FILTER)
  const [activeFilters, setActiveFilters] =
    useState<RobotEventFilterValues>(DEFAULT_FILTER)
  const [pendingRobotEvent, setPendingRobotEvent] =
    useState<RobotEventRow | null>(null)

  const toEventTypeValue = useCallback(
    (type: RobotEventRow['eventType']) => (type === 'Khẩn cấp' ? 1 : 2),
    [],
  )

  const buildUpdatePayload = useCallback(
    (event: RobotEventRow, priority?: number, active?: number) => ({
      eventId: event.eventId || event.id,
      eventType: toEventTypeValue(event.eventType),
      startDate: event.startDate,
      endDate: event.endDate,
      startTime: event.startTime,
      endTime: event.endTime,
      priority,
      active,
    }),
    [toEventTypeValue],
  )

  const toUtcStartOfDay = useCallback(
    (date: Date) => formatStartDate(dayjs(date).startOf('day').toDate()),
    [],
  )

  const toUtcEndOfDay = useCallback(
    (date: Date) =>
      formatEndDate(dayjs(date).endOf('day').millisecond(0).toDate()),
    [],
  )

  const buildFilterParams = useCallback((): RobotEventsFilterParams => {
    const keyword = activeFilters.keyword.trim()
    const fromDate = activeFilters.time?.from
    const toDate = activeFilters.time?.to

    return {
      page: pageIndex,
      size: pageSize,
      keySearch: keyword || undefined,
      status:
        activeFilters.status === undefined
          ? undefined
          : Number(activeFilters.status),
      eventType:
        activeFilters.eventType === undefined
          ? undefined
          : Number(activeFilters.eventType),
      startDateFrom: fromDate ? toUtcStartOfDay(fromDate) : undefined,
      startDateTo: toDate
        ? toUtcEndOfDay(toDate)
        : fromDate
          ? toUtcEndOfDay(fromDate)
          : undefined,
    }
  }, [activeFilters, pageIndex, pageSize, toUtcEndOfDay, toUtcStartOfDay])

  const toggleEventStatusMutation = useMutation({
    mutationFn: async (event: RobotEventRow) => {
      const newActive = event.active ? 0 : 1

      return await updateRobotEvent(
        robotId,
        event.id,
        buildUpdatePayload(event, event.priority ?? 0, newActive),
      )
    },
    onSuccess: () => {
      notification.success({
        title: 'Cập nhật trạng thái sự kiện thành công!',
      })
      void refreshEventsList()
    },
  })

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['robot-events', robotId, pageIndex, pageSize, activeFilters],
    queryFn: ({ signal }) =>
      fetchRobotEvents(robotId, buildFilterParams(), signal),
    enabled: !!robotId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  })

  const refreshEventsList = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['robot-events', robotId],
    })
  }, [queryClient, robotId])

  const rows = useMemo<RobotEventRow[]>(() => {
    if (!data?.items) return []
    return data.items.map((item) => ({
      ...item,
      active: item.active === 1,
    }))
  }, [data?.items])

  const deleteMutation = useMutation({
    mutationFn: (event: RobotEventRow) => deleteRobotEvent(robotId, event.id),
    onSuccess: async () => {
      notification.success({ title: 'Đã xóa sự kiện' })
      await refreshEventsList()
      setDeletingEvent(null)
    },
    onError: (error: unknown) => {
      notification.error({
        title: error instanceof Error ? error.message : 'Không thể xóa sự kiện',
      })
      setDeletingEvent(null)
    },
  })

  const handleView = useCallback((event: RobotEventRow) => {
    setSelectedEvent(event)
    setDrawerMode('view')
    setIsCreateDrawerOpen(true)
  }, [])

  const handleUpdate = useCallback((event: RobotEventRow) => {
    setSelectedEvent(event)
    setDrawerMode('update')
    setIsCreateDrawerOpen(true)
  }, [])

  const handleDelete = useCallback((event: RobotEventRow) => {
    setDeletingEvent(event)
  }, [])

  const handleToggleStatus = useCallback((event: RobotEventRow) => {
    setPendingRobotEvent(event)
  }, [])

  const handleToggleFilter = useCallback(() => {
    setShowFilter((v) => !v)
  }, [])

  const handleOpenCreateDrawer = useCallback(() => {
    setDrawerMode('create')
    setSelectedEvent(null)
    setIsCreateDrawerOpen(true)
  }, [])

  const handleApplyFilters = useCallback(() => {
    const trimmedFilter = {
      ...filter,
      keyword: filter.keyword.trim(),
    }
    setFilter(trimmedFilter)
    setActiveFilters(trimmedFilter)
    setPagination((s) => ({ ...s, pageIndex: 0 }))
    refetch()
  }, [filter])

  const handleConfirmToggleStatus = useCallback(() => {
    if (!pendingRobotEvent) return
    toggleEventStatusMutation.mutate(pendingRobotEvent)
    setPendingRobotEvent(null)
  }, [pendingRobotEvent, toggleEventStatusMutation])

  const handleCloseDeleteModal = useCallback(() => {
    if (deleteMutation.isPending) return
    setDeletingEvent(null)
  }, [deleteMutation.isPending])

  const handleConfirmDelete = useCallback(() => {
    if (!deletingEvent) return
    deleteMutation.mutate(deletingEvent)
  }, [deletingEvent, deleteMutation])

  const handleCloseCreateDrawer = useCallback(() => {
    setIsCreateDrawerOpen(false)
  }, [])

  const handleCloseToggleConfirm = useCallback(() => {
    setPendingRobotEvent(null)
  }, [])

  const handlePageChange = useCallback((p: number) => {
    setPagination((s) => ({ ...s, pageIndex: p - 1 }))
  }, [])

  const handlePageSizeChange = useCallback((size: number) => {
    setPagination({ pageIndex: 0, pageSize: size })
  }, [])

  const columns = useMemo(
    () =>
      buildRobotEventColumns({
        pageIndex,
        pageSize,
        onViewDetail: handleView,
        onUpdate: handleUpdate,
        onDelete: handleDelete,
        onToggleStatus: handleToggleStatus,
      }),
    [
      pageIndex,
      pageSize,
      handleView,
      handleUpdate,
      handleDelete,
      handleToggleStatus,
    ],
  )

  // Kết nối API xuất Excel thông qua hàm exportEventRobot
  const handleExportApi = useCallback(
    async (payload: ExportPayload) => {
      const filterParams = buildFilterParams()
      try {
        setIsExporting(true)
        await exportEventRobot(
          {
            robotId,
            page: filterParams.page,
            size: filterParams.size,
            keySearch: filterParams.keySearch,
            status: filterParams.status,
            eventType: filterParams.eventType,
            startDateFrom: filterParams.startDateFrom,
            startDateTo: filterParams.startDateTo,
          },
          payload,
          'Danh_sach_su_kien_robot',
        )
      } finally {
        setIsExporting(false)
      }
    },
    [robotId, buildFilterParams],
  )

  const { handleExport } = useTableExport<RobotEventRow>(
    'Danh sách sự kiện robot',
    columns,
    handleExportApi,
  )

  return (
    <section className="space-y-4">
      <div className="px-6 !mb--7">
        <PageHeader
          title="DANH SÁCH SỰ KIỆN"
          onToggleFilter={handleToggleFilter}
          onExport={canExport ? handleExport : undefined}
          disabled={!(rows.length > 0)}
          isExporting={isExporting}
        >
          <Button
            variant="primary"
            size="md"
            className="flex-1 gap-2 sm:flex-none h-10"
            onClick={handleOpenCreateDrawer}
          >
            <Plus size={16} />
            Thêm mới
          </Button>
        </PageHeader>
      </div>

      {showFilter && (
        <RobotEventFilterPanel
          values={filter}
          onChange={setFilter}
          onSearch={handleApplyFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        emptyState="Không có sự kiện"
        tableOptions={{
          state: { pagination: { pageIndex, pageSize } },
          onPaginationChange: setPagination,
          getPaginationRowModel: getPaginationRowModel(),
          getCoreRowModel: getCoreRowModel(),
          manualPagination: true,
          pageCount: data?.totalPages ?? 1,
        }}
        footer={
          <Pagination
            page={pageIndex + 1}
            pageCount={data?.totalPages ?? 1}
            pageSize={pageSize}
            totalCount={data?.total ?? 0}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        }
        className="[&_thead_th]:py-3 [&_thead_th]:px-3 [&_tbody_td]:py-3 [&_tbody_td]:px-3"
      />

      <RobotEventDrawer
        open={isCreateDrawerOpen}
        onClose={handleCloseCreateDrawer}
        robotId={robotId}
        mode={drawerMode}
        eventData={selectedEvent}
        onSaved={refreshEventsList}
      />

      <ConfirmModal
        isOpen={!!pendingRobotEvent}
        title={
          pendingRobotEvent?.active
            ? 'Xác nhận vô hiệu hóa sự kiện'
            : 'Xác nhận kích hoạt sự kiện'
        }
        description={
          pendingRobotEvent?.active ? (
            <>
              Bạn có chắc chắn muốn vô hiệu hóa sự kiện{' '}
              <b>{pendingRobotEvent.name}</b>?
            </>
          ) : (
            <>
              Khi kích hoạt sự kiện này, sự kiện khác đang hoạt động có thể bị
              vô hiệu hóa.
              <br />
              Bạn có chắc chắn muốn <b>kích hoạt</b> sự kiện này?
            </>
          )
        }
        variant={pendingRobotEvent?.active ? undefined : 'info'}
        cancelText="Hủy"
        confirmText="Xác nhận"
        isLoading={toggleEventStatusMutation.isPending}
        onClose={handleCloseToggleConfirm}
        onConfirm={handleConfirmToggleStatus}
      />

      <ConfirmModal
        isOpen={!!deletingEvent}
        title="Xóa sự kiện"
        description={
          deletingEvent ? (
            <>
              Bạn chắc chắn muốn xóa sự kiện <b>{deletingEvent.name}</b>?
            </>
          ) : (
            'Bạn chắc chắn muốn xóa sự kiện này?'
          )
        }
        cancelText="Hủy"
        confirmText="Xác nhận"
        isLoading={deleteMutation.isPending}
        variant="danger"
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </section>
  )
}
